/**
 * Recruiter detail-view for a completed interview session.
 *
 * Layout (Apple-clean B2B, matches the rest of Cevee admin):
 *   Header: candidate label + status pill + persona pill + duration
 *   Tabs:   Overview | Transcript | Audit
 *   Right column: AI scorecard summary + recruiter "Mark reviewed" panel
 *
 * Human-in-the-Loop guard: the candidate-facing follow-up step (kept for
 * later phases) only unlocks once recruiter_reviewed_at is set.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Star,
  Shield,
  MessageCircle,
  Video,
  ChevronUp,
  ChevronDown,
  Film,
  Download,
  Code as CodeIcon,
  PenTool,
  Calculator,
  FileText as FileTextIcon,
} from 'lucide-react';

import { useAuth } from '../../hooks/useAuth';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { RecruiterLoadingScreen, RecruiterPageShell } from '../../components/Recruiter/RecruiterStates';
import {
  getResult,
  downloadRecordingMp4,
  normalizeEvaluationCriteria,
  resolveCriterionLabel,
  submitReview,
  type AuditLogEntry,
  type CriterionScore,
  type EvaluationCriterion,
  type InterviewEvaluation,
  type Recommendation,
  type ResultDetail,
  type TranscriptLine,
} from '../../api/interview';
import SystemDesignViewer, {
  type SystemDesignScene,
} from '../../components/Interview/SystemDesignViewer';
import NumbersViewer from '../../components/Interview/NumbersViewer';
import PlaybackVideo from '../../components/Interview/PlaybackVideo';

type Tab = 'overview' | 'recording' | 'artifact' | 'transcript';

const RECOMMENDATIONS: { id: Recommendation; labelDe: string; labelEn: string; tone: string }[] = [
  { id: 'strong_hire',    labelDe: 'Strong Hire',    labelEn: 'Strong Hire',    tone: 'emerald' },
  { id: 'hire',           labelDe: 'Hire',           labelEn: 'Hire',           tone: 'green'   },
  { id: 'neutral',        labelDe: 'Neutral',        labelEn: 'Neutral',        tone: 'amber'   },
  { id: 'no_hire',        labelDe: 'No Hire',        labelEn: 'No Hire',        tone: 'orange'  },
  { id: 'strong_no_hire', labelDe: 'Strong No Hire', labelEn: 'Strong No Hire', tone: 'red'     },
];


const InterviewReview: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang: 'de' | 'en' = i18n.language.startsWith('de') ? 'de' : 'en';
  const { apiCall, isLoading: authLoading, isSignedIn } = useAuth();
  const { userProfile } = useUserProfile();
  const allowRecordingDownload =
    DEMO_RECORDING_DOWNLOAD && userProfile?.is_admin === true;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ResultDetail | null>(null);
  const [tab, setTab] = useState<Tab>('overview');

  // `silent=true` suppresses the full-screen spinner / error replacement
  // so the background scorecard poll can refresh the page without making
  // it flicker.
  // On a silent reload we merge ONLY the evaluation / summary fields
  // back into existing state instead of replacing the whole object.
  // Why: each fetch produces a fresh recording SAS URL (Azure rotates
  // the signature per call). Replacing `data` would change the
  // `recording_url` string identity on every poll, which in turn
  // re-runs PlaybackVideo's [src]-keyed effect, resets its readiness
  // state, and the "Preparing playback..." overlay would flash back
  // every 6 seconds for as long as the poll runs.
  const load = useCallback(async (silent = false) => {
    if (!sessionId) return;
    try {
      if (!silent) setLoading(true);
      setError(null);
      const d = await getResult(apiCall, sessionId);
      if (silent) {
        // Keep the existing object identity for recording_url(_screen)
        // so PlaybackVideo doesn't reset.
        setData((prev) => {
          if (!prev) return d;
          return {
            ...prev,
            evaluation: d.evaluation,
            summary: d.summary,
            audit: d.audit,
          };
        });
      } else {
        setData(d);
      }
    } catch (e) {
      if (!silent) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [apiCall, sessionId]);

  useEffect(() => {
    if (authLoading) return;
    if (!isSignedIn) {
      navigate('/login');
      return;
    }
    void load();
  }, [authLoading, isSignedIn, load, navigate]);

  // Poll for the scorecard while the eval task is still queued/running
  // server-side. The Celery worker normally lands the evaluation row
  // within 20-60s of the interview ending; queued runs can stretch to a
  // couple minutes. We refetch silently every 6s for up to 3 minutes and
  // stop the moment `evaluation` shows up. Beyond that window manual
  // refresh is the right escalation -- a stuck poll loop just masks a
  // real worker problem.
  useEffect(() => {
    if (!data) return undefined;
    if (data.evaluation) return undefined;
    if (data.summary.status !== 'completed') return undefined;

    let attempts = 0;
    const MAX_ATTEMPTS = 30;
    const interval = window.setInterval(() => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS) {
        window.clearInterval(interval);
        return;
      }
      void load(true);
    }, 6000);
    return () => window.clearInterval(interval);
  }, [data, load]);

  if (loading || authLoading) {
    return <RecruiterLoadingScreen icon={Video} />;
  }
  if (error || !data) {
    return (
      <Shell title={lang === 'de' ? 'Interview-Review' : 'Interview review'}>
        <div className="rounded-3xl bg-white border border-red-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <h3 className="font-semibold text-slate-900">
                {lang === 'de' ? 'Konnte das Interview nicht laden' : 'Could not load interview'}
              </h3>
              <p className="text-sm text-slate-600 mt-1">{error || 'Unknown error'}</p>
              <button
                onClick={() => navigate('/interviews')}
                className="mt-4 px-4 py-2 text-sm font-semibold rounded-xl border border-slate-200 hover:bg-slate-50"
              >
                {lang === 'de' ? 'Zurück zur Übersicht' : 'Back to overview'}
              </button>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  const {
    summary,
    evaluation,
    template,
    session,
    audit,
    recording_url: recordingUrl,
    recording_url_screen: recordingUrlScreen,
  } = data;
  const hasCode = !!(session.code_submissions && session.code_submissions.content);
  const hasCanvas = !!(session.canvas_submissions && session.canvas_submissions.summary);
  const hasNumbers = !!(
    session.numbers_submissions &&
    (session.numbers_submissions.cell_count || 0) > 0
  );
  const hasNotepad = !!(
    session.notepad_submissions &&
    ((session.notepad_submissions.content as string) || '').trim().length > 0
  );
  const hasArtifact = hasCode || hasCanvas || hasNumbers || hasNotepad;

  return (
    <Shell title={lang === 'de' ? 'Interview-Review' : 'Interview review'}>
      <div className="flex items-center justify-between mb-5">
        <button
          onClick={() => navigate('/interviews')}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="w-4 h-4" /> {lang === 'de' ? 'Übersicht' : 'Overview'}
        </button>
      </div>

      <HeaderCard summary={summary} lang={lang} />

      {(session.safety_flags || []).length > 0 && (
        <SafetyBanner flags={session.safety_flags || []} lang={lang} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 mt-5">
        <div className="space-y-4 min-w-0">
          <Tabs
            lang={lang}
            active={tab}
            onChange={setTab}
            hasArtifact={hasArtifact}
            artifactKind={
              [hasCode, hasCanvas, hasNumbers, hasNotepad].filter(Boolean).length > 1
                ? 'multi'
                : hasCode
                  ? 'code'
                  : hasCanvas
                    ? 'canvas'
                    : hasNumbers
                      ? 'numbers'
                      : 'notepad'
            }
          />
          {tab === 'overview' && (
            <OverviewPanel
              evaluation={evaluation}
              criteria={normalizeEvaluationCriteria(template?.evaluation_criteria)}
              lang={lang}
            />
          )}
          {tab === 'recording' && (
            <RecordingPanel
              sessionId={sessionId!}
              recordingUrl={recordingUrl}
              recordingUrlScreen={recordingUrlScreen}
              lang={lang}
              hasScreenShare={!!template?.enable_screen_share}
              allowDownload={allowRecordingDownload}
              apiCall={apiCall}
            />
          )}
          {tab === 'artifact' && (
            <ArtifactPanel
              code={session.code_submissions}
              canvas={session.canvas_submissions}
              numbers={session.numbers_submissions}
              notepad={session.notepad_submissions}
              lang={lang}
            />
          )}
          {tab === 'transcript' && (
            <TranscriptPanel transcript={session.transcript || []} lang={lang} />
          )}

          {audit.length > 0 && (
            <Section
              title={lang === 'de' ? 'Compliance & Audit' : 'Compliance & audit'}
              collapsible
              defaultOpen={false}
            >
              <AuditPanel audit={audit} lang={lang} embedded />
            </Section>
          )}
        </div>

        <ReviewPanel
          evaluation={evaluation}
          sessionId={summary.session_id}
          apiCall={apiCall}
          lang={lang}
          onReviewed={load}
        />
      </div>
    </Shell>
  );
};

// ============================================================
// Header + cards
// ============================================================

const Shell: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <RecruiterPageShell wide contentClassName="max-w-6xl">
    <p className="mb-6 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      Cevee · {title}
    </p>
    {children}
  </RecruiterPageShell>
);

const HeaderCard: React.FC<{ summary: ResultDetail['summary']; lang: 'de' | 'en' }> = ({ summary, lang }) => {
  const recLabel = useMemo(() => {
    const r = summary.final_recommendation || summary.recommendation;
    if (!r) return null;
    const c = RECOMMENDATIONS.find((x) => x.id === r);
    return c ? (lang === 'de' ? c.labelDe : c.labelEn) : r;
  }, [summary, lang]);

  return (
    <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            {summary.template_title || 'Interview'}
            {summary.role_title ? <> &middot; {summary.role_title}</> : null}
            {summary.role_seniority ? <> &middot; {summary.role_seniority}</> : null}
          </p>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight mt-0.5">
            {summary.candidate_label}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{summary.invited_email}</p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Chip>
              <Clock className="w-3 h-3" />
              {summary.duration_seconds != null
                ? `${Math.floor((summary.duration_seconds || 0) / 60)} min`
                : '--'}
            </Chip>
            <Chip>{summary.template_type || '--'}</Chip>
            <Chip>{summary.persona_id}</Chip>
            <Chip tone={summary.status === 'completed' ? 'emerald' : 'amber'}>
              {summary.status}
            </Chip>
            {summary.is_reviewed && (
              <Chip tone="emerald">
                <CheckCircle2 className="w-3 h-3" />
                {lang === 'de' ? 'Geprüft' : 'Reviewed'}
              </Chip>
            )}
          </div>
        </div>

        <div className="text-right">
          {recLabel && (
            <>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                {lang === 'de' ? 'AI Empfehlung' : 'AI recommendation'}
              </p>
              <p
                className={
                  'mt-1.5 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ' +
                  recColor(summary.final_recommendation || summary.recommendation)
                }
              >
                {recLabel}
              </p>
            </>
          )}
          {/* Score + confidence hidden from UI (data still tracked in backend) */}
        </div>
      </div>
    </div>
  );
};

const Tabs: React.FC<{
  lang: 'de' | 'en';
  active: Tab;
  onChange: (t: Tab) => void;
  hasArtifact?: boolean;
  artifactKind?: 'code' | 'canvas' | 'numbers' | 'notepad' | 'multi';
}> = ({ lang, active, onChange, hasArtifact, artifactKind }) => {
  const items: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview',   label: lang === 'de' ? 'Bewertung'  : 'Overview',   icon: <Star className="w-3.5 h-3.5" /> },
    { id: 'recording',  label: lang === 'de' ? 'Aufnahme'   : 'Recording',  icon: <Film className="w-3.5 h-3.5" /> },
    ...(hasArtifact
      ? [{
          id: 'artifact' as Tab,
          label: artifactKind === 'multi'
            ? (lang === 'de' ? 'Artefakte' : 'Artifacts')
            : artifactKind === 'canvas'
              ? (lang === 'de' ? 'Whiteboard' : 'Whiteboard')
              : artifactKind === 'numbers'
                ? (lang === 'de' ? 'Zahlen' : 'Numbers')
                : artifactKind === 'notepad'
                  ? (lang === 'de' ? 'Notizblock' : 'Notepad')
                  : (lang === 'de' ? 'Code' : 'Code'),
          icon: artifactKind === 'canvas'
            ? <PenTool className="w-3.5 h-3.5" />
            : artifactKind === 'numbers'
              ? <Calculator className="w-3.5 h-3.5" />
              : artifactKind === 'notepad'
                ? <FileTextIcon className="w-3.5 h-3.5" />
                : <CodeIcon className="w-3.5 h-3.5" />,
        }]
      : []),
    { id: 'transcript', label: lang === 'de' ? 'Transkript' : 'Transcript', icon: <MessageCircle className="w-3.5 h-3.5" /> },
  ];
  return (
    <div className="scrollbar-hide flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1">
      {items.map((it) => (
        <button
          key={it.id}
          onClick={() => onChange(it.id)}
          className={`flex min-w-fit flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-[13px] font-semibold transition ${
            active === it.id
              ? 'bg-slate-900 text-white'
              : 'text-slate-500 hover:bg-slate-100'
          }`}
        >
          {it.icon}
          {it.label}
        </button>
      ))}
    </div>
  );
};

// ============================================================
// Panels
// ============================================================

const OverviewPanel: React.FC<{
  evaluation: InterviewEvaluation | null;
  criteria: EvaluationCriterion[];
  lang: 'de' | 'en';
}> = ({ evaluation, criteria, lang }) => {
  if (!evaluation) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
        <div className="flex items-start gap-3">
          <Loader2 className="w-5 h-5 text-amber-500 animate-spin mt-0.5" />
          <div>
            <h3 className="font-semibold text-slate-900">
              {lang === 'de' ? 'Scorecard wird erstellt' : 'Scorecard generating'}
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {lang === 'de'
                ? 'Die KI bewertet das Transcript gerade. Das dauert in der Regel weniger als eine Minute.'
                : 'The AI is evaluating the transcript. This usually takes less than a minute.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // `_persist_no_data` sentinel: confidence == 0 + no overall_score means the
  // session has no transcript at all (candidate dropped before speaking).
  // Show an explicit "abandoned" banner instead of a misleading neutral.
  const isAbandonedNoTranscript =
    evaluation.ai_confidence === 0 &&
    evaluation.overall_score == null &&
    (evaluation.criteria_scores == null || evaluation.criteria_scores.length === 0);

  if (isAbandonedNoTranscript) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0 mt-0.5">
            !
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">
              {lang === 'de'
                ? 'Kein Transkript - Interview abgebrochen'
                : 'No transcript - interview abandoned'}
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {lang === 'de'
                ? 'Es wurde kein Audio aufgezeichnet. Wahrscheinlich hat der oder die Kandidat:in das Interview verlassen, bevor das erste Mal gesprochen wurde. Eine automatische Bewertung ist deshalb nicht möglich. Du kannst die Session über das Review-Panel rechts manuell einordnen.'
                : 'No audio was captured. The candidate most likely closed the tab before speaking, so an automatic evaluation is not possible. You can still mark the outcome manually via the review panel on the right.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const criteriaScores = evaluation.criteria_scores || [];

  return (
    <div className="space-y-4">
      {/* Per-criterion scores — primary signal, kept open */}
      <Section title={lang === 'de' ? 'Kriterien' : 'Criteria'} collapsible defaultOpen>
        {criteriaScores.length === 0 ? (
          <p className="text-sm text-slate-500">
            {lang === 'de' ? 'Keine Kriterien bewertet.' : 'No criteria scored.'}
          </p>
        ) : (
          <div className="space-y-3">
            {criteriaScores.map((c, i) => (
              <CriterionRow
                key={c.id || String(i)}
                criterion={c}
                label={resolveCriterionLabel(c, criteria, i)}
              />
            ))}
          </div>
        )}
      </Section>

      {evaluation.strengths && (
        <Section title={lang === 'de' ? 'Stärken' : 'Strengths'} collapsible defaultOpen>
          <Prose text={evaluation.strengths} />
        </Section>
      )}
      {evaluation.weaknesses && (
        <Section title={lang === 'de' ? 'Entwicklungsfelder' : 'Areas to develop'} collapsible defaultOpen>
          <Prose text={evaluation.weaknesses} />
        </Section>
      )}
      {evaluation.detailed_feedback && (
        <Section title={lang === 'de' ? 'Ausführliche Bewertung' : 'Detailed feedback'} collapsible defaultOpen={false}>
          <Prose text={evaluation.detailed_feedback} />
        </Section>
      )}
      {evaluation.explanation_text && (
        <Section title={lang === 'de' ? 'Erklärung für Kandidat:in' : 'Candidate-facing explanation'} collapsible defaultOpen={false}>
          <Prose text={evaluation.explanation_text} muted />
        </Section>
      )}
    </div>
  );
};

const CriterionRow: React.FC<{ criterion: CriterionScore; label: string }> = ({ criterion, label }) => {
  const [open, setOpen] = useState(false);
  const pct = Math.min(1, Math.max(0, (criterion.score || 0) / 5));
  return (
    <div className="border border-slate-200 rounded-2xl p-3">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-slate-900 truncate">{label}</p>
            <span className="text-xs font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-full">
              {criterion.score} / 5
            </span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-pink-500 to-orange-400"
              style={{ width: `${pct * 100}%` }}
            />
          </div>
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-slate-400 hover:text-slate-600"
          aria-label={open ? 'Hide details' : 'Show details'}
        >
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-600">
          <p className="leading-relaxed">{criterion.justification}</p>
          {criterion.evidence?.length ? (
            <ul className="mt-2 space-y-1">
              {criterion.evidence.map((q, i) => (
                <li key={i} className="text-xs text-slate-500 pl-3 border-l-2 border-slate-200 italic">
                  &quot;{q}&quot;
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  );
};

const SafetyBanner: React.FC<{
  flags: { category: string; actor: string; rule: string; snippet: string; ts_ms: number }[];
  lang: 'de' | 'en';
}> = ({ flags, lang }) => {
  const byCategory = flags.reduce<Record<string, number>>((acc, f) => {
    acc[f.category] = (acc[f.category] || 0) + 1;
    return acc;
  }, {});
  const ai_drift = flags.some((f) => f.category === 'forbidden_topic' && f.actor === 'ai');
  const labels: Record<string, { de: string; en: string }> = {
    prompt_injection: {
      de: 'Prompt-Injection-Versuche der Kandidat:in',
      en: 'Candidate prompt-injection attempts',
    },
    forbidden_topic: {
      de: ai_drift ? 'Verbotene Themen (AI hat möglicherweise gedriftet)' : 'Verbotene Themen erwähnt',
      en: ai_drift ? 'Forbidden topics (AI may have drifted)' : 'Forbidden topics mentioned',
    },
  };
  return (
    <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3.5 shadow-[0_2px_8px_rgba(180,83,9,0.08)]">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-amber-900">
            {lang === 'de' ? 'Sicherheits-Hinweise' : 'Safety notices'} ({flags.length})
          </p>
          <p className="mt-0.5 text-xs text-amber-800/80 leading-relaxed">
            {lang === 'de'
              ? 'Diese Hinweise sind informativ. Sie blockieren das Interview nicht. Prüfe die Transcript-Stellen vor einer Entscheidung.'
              : 'These notices are informational. They never block the interview. Review the transcript before deciding.'}
          </p>
          <ul className="mt-2 space-y-1">
            {Object.entries(byCategory).map(([cat, n]) => (
              <li key={cat} className="text-xs text-amber-900">
                <span className="font-medium">{labels[cat]?.[lang] || cat}</span>{' '}
                <span className="text-amber-700/80">({n}x)</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// TODO(demo): remove after demo — re-enable streaming-only (no download buttons).
const DEMO_RECORDING_DOWNLOAD = true;

function saveBlobDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

const RecordingPanel: React.FC<{
  sessionId: string;
  recordingUrl: string | null;
  recordingUrlScreen?: string | null;
  lang: 'de' | 'en';
  hasScreenShare: boolean;
  allowDownload: boolean;
  apiCall: ReturnType<typeof useAuth>['apiCall'];
}> = ({
  sessionId,
  recordingUrl,
  recordingUrlScreen,
  hasScreenShare,
  lang,
  allowDownload,
  apiCall,
}) => {
  const [downloading, setDownloading] = useState<string | null>(null);

  const downloadMp4 = async (kind: 'cam' | 'screen') => {
    const key = `${kind}-mp4`;
    setDownloading(key);
    try {
      const blob = await downloadRecordingMp4(apiCall, sessionId, kind);
      saveBlobDownload(blob, `interview-${sessionId}-${kind}.mp4`);
    } catch (e) {
      console.error('MP4 export failed', e);
      window.alert(
        lang === 'de'
          ? `MP4-Export fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`
          : `MP4 export failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setDownloading(null);
    }
  };
  if (!recordingUrl) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)] text-sm text-slate-500">
        <div className="flex items-start gap-3">
          <Film className="w-5 h-5 text-slate-400 mt-0.5" />
          <div>
            <p className="font-semibold text-slate-700">
              {lang === 'de' ? 'Keine Aufnahme verfügbar' : 'No recording available'}
            </p>
            <p className="mt-1 text-xs text-slate-500 leading-relaxed">
              {lang === 'de'
                ? 'Entweder wurde keine Aufnahme-Einwilligung erteilt, das Interview wurde vor dem ersten Chunk abgebrochen, oder die Retention-Frist ist abgelaufen.'
                : 'Either no recording consent was granted, the interview was aborted before the first chunk, or the retention period expired.'}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-3xl bg-black border border-slate-200/70 overflow-hidden shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
      <PlaybackVideo
        src={recordingUrl}
        srcScreen={recordingUrlScreen}
        fallback={
          lang === 'de'
            ? 'Dein Browser unterstützt das Video-Tag nicht.'
            : 'Your browser does not support the video tag.'
        }
      />
      <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400 bg-slate-950/40 border-t border-slate-800/50">
        <p>
          {lang === 'de'
            ? `Aufnahme enthält Kandidat:in (Bild + Ton)${hasScreenShare ? ' + Bildschirm' : ''} und AI-Stimme. Link ist 60 Minuten gültig.`
            : `Recording contains candidate (video + audio)${hasScreenShare ? ' + screen' : ''} + AI voice. Link is valid for 60 minutes.`}
        </p>
        {allowDownload && (
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              type="button"
              disabled={downloading === 'cam-mp4'}
              onClick={() => void downloadMp4('cam')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/15 disabled:opacity-50 text-white/80 hover:text-white text-[11px] font-semibold transition-colors"
            >
              {downloading === 'cam-mp4' ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              {lang === 'de' ? 'Kamera MP4' : 'Camera MP4'}
            </button>
            {recordingUrlScreen && (
              <button
                type="button"
                disabled={downloading === 'screen-mp4'}
                onClick={() => void downloadMp4('screen')}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/15 disabled:opacity-50 text-white/80 hover:text-white text-[11px] font-semibold transition-colors"
              >
                {downloading === 'screen-mp4' ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                {lang === 'de' ? 'Bildschirm MP4' : 'Screen MP4'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Shared playback component with speed pills (0.5x/1x/1.5x/2x).

const ArtifactPanel: React.FC<{
  code: {
    language?: string;
    content?: string;
    char_count?: number;
    updated_at_ms?: number;
  } | null;
  canvas: {
    kind?: string;
    scene?: unknown;
    summary?: string;
    element_count?: number;
    updated_at_ms?: number;
  } | null;
  numbers: {
    rows?: number;
    cols?: number;
    cells?: Record<string, { raw?: string; value?: string | number | null }>;
    summary?: string;
    cell_count?: number;
    updated_at_ms?: number;
  } | null;
  notepad: {
    content?: string;
    char_count?: number;
    updated_at_ms?: number;
  } | null;
  lang: 'de' | 'en';
}> = ({ code, canvas, numbers, notepad, lang }) => {
  const hasCode = !!code?.content;
  const hasCanvas = !!canvas?.summary;
  const hasNumbers = !!(numbers && (numbers.cell_count || 0) > 0);
  const hasNotepad = !!(notepad && (notepad.content || '').trim().length > 0);
  if (!hasCode && !hasCanvas && !hasNumbers && !hasNotepad) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)] text-sm text-slate-500">
        {lang === 'de' ? 'Keine Artefakte für diese Session.' : 'No artifacts for this session.'}
      </div>
    );
  }
  return (
    <div className="space-y-5">
      {hasCode && <CodeArtifactCard code={code!} lang={lang} />}
      {hasCanvas && <CanvasArtifactCard canvas={canvas!} lang={lang} />}
      {hasNumbers && <NumbersArtifactCard numbers={numbers!} lang={lang} />}
      {hasNotepad && <NotepadArtifactCard notepad={notepad!} lang={lang} />}
    </div>
  );
};

const NotepadArtifactCard: React.FC<{
  notepad: NonNullable<React.ComponentProps<typeof ArtifactPanel>['notepad']>;
  lang: 'de' | 'en';
}> = ({ notepad, lang }) => {
  return (
    <div className="rounded-3xl bg-[#fffbe6] border border-yellow-300/60 overflow-hidden shadow-[0_2px_8px_rgba(217,119,6,0.06)]">
      <div className="flex items-center justify-between px-4 py-3 bg-yellow-100/70 border-b border-yellow-300/60">
        <div className="flex items-center gap-2">
          <FileTextIcon className="w-4 h-4 text-yellow-800" />
          <span className="text-sm font-semibold text-yellow-900">
            {lang === 'de' ? 'Notizblock' : 'Notepad'}
          </span>
        </div>
        <span className="text-[11px] text-yellow-800/70">
          {(notepad.char_count ?? (notepad.content || '').length).toLocaleString()}{' '}
          {lang === 'de' ? 'Zeichen' : 'chars'}
        </span>
      </div>
      <pre className="text-[13px] leading-6 px-6 py-4 text-amber-950 whitespace-pre-wrap font-[ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace] max-h-[60vh] overflow-y-auto">
        {notepad.content}
      </pre>
      <div className="px-4 py-2 text-[11px] text-yellow-800/60 bg-yellow-100/40 border-t border-yellow-300/60">
        {lang === 'de'
          ? 'Freie Notizen, die der/die Kandidat:in waehrend des Gespraechs gemacht hat. Die KI hat den Text als Kontext mitgesehen.'
          : 'Free-form notes the candidate jotted while talking. The AI saw the text as context.'}
      </div>
    </div>
  );
};

const NumbersArtifactCard: React.FC<{
  numbers: NonNullable<React.ComponentProps<typeof ArtifactPanel>['numbers']>;
  lang: 'de' | 'en';
}> = ({ numbers, lang }) => {
  return (
    <div className="rounded-3xl bg-white border border-slate-200/70 overflow-hidden shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-slate-600" />
          <span className="text-sm font-semibold text-slate-800">
            {lang === 'de' ? 'Zahlen-Pane' : 'Numbers pane'}
          </span>
        </div>
        <span className="text-[11px] text-slate-500">
          {numbers.cell_count ?? 0} {lang === 'de' ? 'Zellen' : 'cells'}
        </span>
      </div>
      <div className="bg-slate-950 p-1">
        <NumbersViewer
          snapshot={numbers}
          lang={lang}
          height={420}
        />
      </div>
      {numbers.summary && (
        <details className="group">
          <summary className="cursor-pointer list-none px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 hover:bg-slate-50 border-t border-slate-200">
            {lang === 'de' ? 'Text-Zusammenfassung (für die KI)' : 'Text summary (for the AI)'}
          </summary>
          <pre className="text-[12.5px] leading-relaxed p-4 bg-slate-50 text-slate-800 overflow-x-auto max-h-[40vh] whitespace-pre-wrap font-mono">
            {numbers.summary}
          </pre>
        </details>
      )}
      <div className="px-4 py-2 text-[11px] text-slate-400 bg-slate-50 border-t border-slate-200">
        {lang === 'de'
          ? 'Finaler Stand der Rechnungen beim Interview-Ende. Formeln zeigen den Ausdruck im Tooltip + den ausgewerteten Wert.'
          : 'Final calculation state at the end of the interview. Formula cells show the expression in the tooltip + the evaluated value.'}
      </div>
    </div>
  );
};

const CodeArtifactCard: React.FC<{
  code: NonNullable<React.ComponentProps<typeof ArtifactPanel>['code']>;
  lang: 'de' | 'en';
}> = ({ code, lang }) => {
  const language = code.language || 'plaintext';
  return (
    <div className="rounded-3xl bg-white border border-slate-200/70 overflow-hidden shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <CodeIcon className="w-4 h-4 text-slate-600" />
          <span className="text-sm font-semibold text-slate-800">
            {lang === 'de' ? 'Finaler Code' : 'Final code'}
          </span>
          <span className="ml-1 text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">
            {language}
          </span>
        </div>
        <span className="text-[11px] text-slate-500">
          {(code.char_count || (code.content || '').length).toLocaleString()} {lang === 'de' ? 'Zeichen' : 'chars'}
        </span>
      </div>
      <pre className="text-[12.5px] leading-relaxed font-mono p-4 bg-slate-950 text-slate-100 overflow-x-auto max-h-[70vh]">
        <code>{code.content}</code>
      </pre>
      <div className="px-4 py-2 text-[11px] text-slate-400 bg-slate-50 border-t border-slate-200">
        {lang === 'de'
          ? 'Letzter Editor-Stand zum Zeitpunkt des Interview-Abschlusses. Bewerte den Code zusammen mit dem Transcript.'
          : 'Final editor state at the moment the interview ended. Evaluate together with the transcript.'}
      </div>
    </div>
  );
};

const CanvasArtifactCard: React.FC<{
  canvas: NonNullable<React.ComponentProps<typeof ArtifactPanel>['canvas']>;
  lang: 'de' | 'en';
}> = ({ canvas, lang }) => {
  // New react-flow scenes ship `scene.nodes` + `scene.edges`. Older
  // textarea-fallback sessions only have `scene.notes` or just the
  // pre-formatted `summary` -- we degrade gracefully.
  const scene = (canvas.scene as SystemDesignScene | null | undefined) ?? null;
  const hasVisualScene = !!(scene && Array.isArray(scene.nodes) && scene.nodes.length > 0);
  return (
    <div className="rounded-3xl bg-white border border-slate-200/70 overflow-hidden shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <PenTool className="w-4 h-4 text-slate-600" />
          <span className="text-sm font-semibold text-slate-800">
            {lang === 'de' ? 'Whiteboard' : 'Whiteboard'}
          </span>
        </div>
        <span className="text-[11px] text-slate-500">
          {canvas.element_count ?? 0} {lang === 'de' ? 'Elemente' : 'elements'}
        </span>
      </div>
      {hasVisualScene && (
        <div className="bg-slate-50 border-b border-slate-200">
          <SystemDesignViewer scene={scene} height={480} />
        </div>
      )}
      <details className={hasVisualScene ? 'group' : 'group [&]:open'}>
        <summary
          className={[
            'cursor-pointer list-none px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 hover:bg-slate-50',
            hasVisualScene ? 'border-t border-slate-200' : '',
          ].join(' ')}
        >
          {lang === 'de' ? 'Text-Zusammenfassung (für die KI)' : 'Text summary (for the AI)'}
        </summary>
        <pre className="text-[12.5px] leading-relaxed p-4 bg-slate-50 text-slate-800 overflow-x-auto max-h-[40vh] whitespace-pre-wrap font-sans">
          {canvas.summary}
        </pre>
      </details>
      <div className="px-4 py-2 text-[11px] text-slate-400 bg-slate-50 border-t border-slate-200">
        {hasVisualScene
          ? lang === 'de'
            ? 'Finaler Stand des Whiteboards beim Interview-Ende. Die Text-Zusammenfassung ist genau das was die KI während des Interviews gesehen hat.'
            : 'Final whiteboard state at the end of the interview. The text summary is exactly what the AI saw during the session.'
          : lang === 'de'
            ? 'Text-Repräsentation des Whiteboards (Komponenten + Verbindungen). Diese ältere Session hat keine visuelle Skizze gespeichert.'
            : 'Text representation of the whiteboard (components + connections). This older session has no visual sketch stored.'}
      </div>
    </div>
  );
};

const TranscriptPanel: React.FC<{ transcript: TranscriptLine[]; lang: 'de' | 'en' }> = ({
  transcript,
  lang,
}) => {
  if (!transcript || transcript.length === 0) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)] text-sm text-slate-500">
        {lang === 'de'
          ? 'Es ist kein Transcript gespeichert. Vielleicht war es ein sehr kurzes Interview oder der Stream wurde abgebrochen.'
          : 'No transcript stored for this session.'}
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-white border border-slate-200/70 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.04)] space-y-2 max-h-[640px] overflow-y-auto">
      {transcript.map((l, i) => (
        <div key={i} className={`flex ${l.speaker === 'candidate' ? 'justify-end' : 'justify-start'}`}>
          <div
            className={
              l.speaker === 'candidate'
                ? 'max-w-[78%] px-3.5 py-2 rounded-2xl rounded-br-md bg-gradient-to-br from-pink-500/10 to-orange-400/10 text-slate-800 border border-pink-200/50 text-sm leading-relaxed'
                : l.speaker === 'system'
                ? 'max-w-[78%] px-3 py-2 rounded-2xl bg-amber-50 text-amber-700 border border-amber-200 text-xs'
                : 'max-w-[78%] px-3.5 py-2 rounded-2xl rounded-bl-md bg-slate-50 border border-slate-200 text-slate-800 text-sm leading-relaxed'
            }
          >
            {l.text}
          </div>
        </div>
      ))}
    </div>
  );
};

const AuditPanel: React.FC<{ audit: AuditLogEntry[]; lang: 'de' | 'en'; embedded?: boolean }> = ({ audit, lang, embedded }) => {
  const inner = audit.length === 0 ? (
    <div className="text-sm text-slate-500">
      {lang === 'de' ? 'Keine Audit-Einträge' : 'No audit entries'}
    </div>
  ) : (
    <div className={`divide-y divide-slate-100 ${embedded ? 'max-h-80 overflow-y-auto' : 'max-h-[640px] overflow-y-auto'}`}>
      {audit.map((a) => (
        <div key={a.id} className="py-3 text-sm first:pt-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {a.actor_type || 'system'}
              </span>
              <span className="font-medium text-slate-800">{a.action}</span>
            </div>
            <span className="text-xs text-slate-400">{new Date(a.created_at * 1000).toLocaleString()}</span>
          </div>
          {a.details && Object.keys(a.details).length > 0 && (
            <pre className="mt-1.5 text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded-xl px-2.5 py-2 overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(a.details, null, 2)}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
  if (embedded) return inner;
  return (
    <div className="rounded-3xl bg-white border border-slate-200/70 shadow-[0_2px_8px_rgba(15,23,42,0.04)] p-5">
      {inner}
    </div>
  );
};

// ============================================================
// Review panel (Human-in-the-Loop)
// ============================================================

const ReviewPanel: React.FC<{
  evaluation: InterviewEvaluation | null;
  sessionId: string;
  apiCall: ReturnType<typeof useAuth>['apiCall'];
  lang: 'de' | 'en';
  onReviewed: () => void;
}> = ({ evaluation, sessionId, apiCall, lang, onReviewed }) => {
  const [overrideRec, setOverrideRec] = useState<Recommendation | ''>('');
  const [overrideScore, setOverrideScore] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (evaluation) {
      setOverrideRec((evaluation.recruiter_override_recommendation || '') as Recommendation | '');
      setOverrideScore(
        evaluation.recruiter_override_score !== null && evaluation.recruiter_override_score !== undefined
          ? String(evaluation.recruiter_override_score)
          : '',
      );
      setNotes(evaluation.recruiter_notes || '');
    }
  }, [evaluation]);

  const handleSubmit = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const payload: Record<string, unknown> = { notes: notes || null };
      if (overrideRec) payload.override_recommendation = overrideRec;
      if (overrideScore) payload.override_score = parseInt(overrideScore, 10);
      await submitReview(apiCall, sessionId, payload);
      onReviewed();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [apiCall, sessionId, overrideRec, overrideScore, notes, onReviewed]);

  const isReviewed = !!evaluation?.is_reviewed;
  const canSubmit = !!evaluation && !busy;

  return (
    <div className="rounded-3xl bg-white border border-slate-200/70 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.04)] h-fit lg:sticky lg:top-5">
      <div className="flex items-start gap-2.5">
        <Shield className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-slate-900">
            {lang === 'de' ? 'Recruiter Review' : 'Recruiter review'}
          </h3>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            {lang === 'de'
              ? 'Human-in-the-Loop: deine Entscheidung überschreibt die KI. DSGVO Art. 22.'
              : 'Human-in-the-loop: your decision overrides the AI. GDPR Art. 22.'}
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <Field label={lang === 'de' ? 'Empfehlung' : 'Recommendation'}>
          <select
            value={overrideRec}
            onChange={(e) => setOverrideRec(e.target.value as Recommendation | '')}
            className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-pink-200"
            disabled={busy}
          >
            <option value="">{lang === 'de' ? '- AI Vorschlag akzeptieren -' : '- accept AI suggestion -'}</option>
            {RECOMMENDATIONS.map((r) => (
              <option key={r.id} value={r.id}>{lang === 'de' ? r.labelDe : r.labelEn}</option>
            ))}
          </select>
        </Field>

        <Field label={lang === 'de' ? 'Score (0-100, optional)' : 'Score (0-100, optional)'}>
          <input
            type="number"
            min={0}
            max={100}
            value={overrideScore}
            onChange={(e) => setOverrideScore(e.target.value)}
            placeholder={evaluation?.overall_score !== null && evaluation?.overall_score !== undefined ? String(evaluation.overall_score) : '--'}
            className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-pink-200"
            disabled={busy}
          />
        </Field>

        <Field label={lang === 'de' ? 'Notiz (intern)' : 'Notes (internal)'}>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-pink-200 resize-y"
            placeholder={lang === 'de' ? 'Begründung / Beobachtungen …' : 'Reasoning / observations …'}
            disabled={busy}
          />
        </Field>

        {err && (
          <div className="flex items-start gap-2 p-2.5 rounded-xl bg-red-50 border border-red-200">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{err}</p>
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {isReviewed
            ? (lang === 'de' ? 'Aktualisieren' : 'Update review')
            : (lang === 'de' ? 'Als geprüft markieren' : 'Mark as reviewed')}
        </button>

        {isReviewed && evaluation?.recruiter_reviewed_at && (
          <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-2.5 py-1.5 flex items-center gap-1.5">
            <CheckCircle2 className="w-3 h-3" />
            {lang === 'de' ? 'Geprüft am ' : 'Reviewed on '}
            {new Date(evaluation.recruiter_reviewed_at * 1000).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Small UI primitives
// ============================================================

const Section: React.FC<{
  title: string;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
}> = ({ title, children, collapsible = false, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!collapsible) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200/70 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-3">{title}</p>
        {children}
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-3xl bg-white border border-slate-200/70 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-5 py-4 text-left transition-colors hover:bg-slate-50"
      >
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{title}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
};

const Prose: React.FC<{ text: string; muted?: boolean }> = ({ text, muted }) => (
  <p className={`text-sm whitespace-pre-line leading-relaxed ${muted ? 'text-slate-500 italic' : 'text-slate-700'}`}>
    {text}
  </p>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
      {label}
    </label>
    {children}
  </div>
);

const Chip: React.FC<{ children: React.ReactNode; tone?: 'emerald' | 'amber' | 'slate' }> = ({
  children,
  tone = 'slate',
}) => {
  const cls =
    tone === 'emerald'
      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
      : tone === 'amber'
      ? 'bg-amber-50 text-amber-700 border border-amber-200'
      : 'bg-slate-50 text-slate-700 border border-slate-200';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`}>
      {children}
    </span>
  );
};

function recColor(r: Recommendation | null | undefined): string {
  switch (r) {
    case 'strong_hire':    return 'bg-emerald-100 text-emerald-700';
    case 'hire':           return 'bg-green-100   text-green-700';
    case 'neutral':        return 'bg-amber-100   text-amber-700';
    case 'no_hire':        return 'bg-orange-100  text-orange-700';
    case 'strong_no_hire': return 'bg-red-100     text-red-700';
    default:               return 'bg-slate-100   text-slate-600';
  }
}

export default InterviewReview;
